// api/tours.js
import apiClient from "./client";
import { withCustomerLimit } from "./customerLimits";

export function fetchTours(siteId) {
  return apiClient.get(`/tour/site/${siteId}`).then((res) => res.data);
}

export function createTour(payload) {
  return withCustomerLimit({ siteId: payload.siteId }, 'tours',
    () => apiClient.post(`/tour`, payload).then((res) => res.data));
}

export function duplicateTour(tourId) {
  return withCustomerLimit({ tourId }, 'tours',
    () => apiClient.post(`/tour/${tourId}/copy`).then((res) => res.data), { checkContent: true });
}

export function deleteTour(tourId) {
  return apiClient.delete(`/tour/${tourId}`);
}

// Get a fully-hydrated tour view for editor
export function getTourView({ siteId, tourId, lang, type }) {
  const params = {};
  if (lang) params.lang = lang;
  if (type) params.type = type;

  return apiClient.get(`tour/site/${siteId}/tour/${tourId}/view`, { params }).then((r) => r.data);
}

// Save a single step’s language content (title + paragraphs + audio bindings)
export function saveStepContent({ tourId, stepId, language, payload }) {
  // payload shape below in the component
  return apiClient
    .put(`/tour/${tourId}/step/${stepId}/content`, {
      language,
      ...payload,
    })
    .then((r) => r.data);
}

// Publish/Unpublish tour
export function publishTour({ tourId, publish = true }) {
  return apiClient.put(`/tour/${tourId}/publish`, { publish }).then((r) => r.data);
}

export function setStepPublished({ tourId, stepId, publish }) {
  return apiClient.put(`/tour/${tourId}/step/${stepId}/publish`, { publish }).then((r) => r.data);
}

// Lock/unlock a step's audio. When locked, AI narration generation/translation
// will not overwrite this step's audio (governed by the English track).
export function setStepAudioLock({ tourId, stepId, locked }) {
  return apiClient
    .put(`/tour/${tourId}/step/${stepId}/audio-lock`, { locked })
    .then((r) => r.data);
}

export function updateTourSettings({
  tourId,
  keepBackgroundAudioAfterNarration,
  chantingOffByDefault,
  chantVolume,
  chantDucking,
  imagePopupEnabled,
  carouselEnabled,
  wayfindingEnabled,
  gpsEnabled,
  routeGeoJson,
  description,
  iconKey,
  name,
  defaultLanguageCode,
  narrationVoice,
  pronunciations,
}) {
  return apiClient
    .put(`/tour/${tourId}/settings`, {
      keepBackgroundAudioAfterNarration,
      chantingOffByDefault,
      chantVolume,
      chantDucking,
      imagePopupEnabled,
      carouselEnabled,
      wayfindingEnabled,
      gpsEnabled,
      routeGeoJson,
      description,
      iconKey,
      name,
      defaultLanguageCode,
      narrationVoice,
      pronunciations,
    })
    .then((r) => r.data);
}

export function uploadMedia(payload) {
  if (payload.role === 'audio' || payload.role === 'bg_audio') return uploadMediaUnchecked(payload);
  return withCustomerLimit({ siteId: payload.siteId, tourId: payload.tourId, stepId: payload.stepId },
    'images', () => uploadMediaUnchecked(payload));
}

async function uploadMediaUnchecked({ siteId, tourId, stepId, role, file, language }) {
  const fd = new FormData();
  fd.append("SiteId", siteId.toString());
  fd.append("TourId", tourId.toString());
  fd.append("StepId", stepId.toString());
  fd.append("Role", role);
  fd.append("File", file);
  if (language) fd.append("Language", language);

  // Choose the correct endpoint based on media type
  const isAudio = role === "audio" || role === "bg_audio";
  const endpoint = isAudio ? "/tours/media/upload-audio" : "/tours/media/upload-image";

  try {
    const res = await apiClient.post(endpoint, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // Axios automatically throws for non-2xx; this is just for clarity
    if (res.status < 200 || res.status >= 300) {
      console.error("Upload failed:", res);
      throw new Error(res.statusText || "Upload failed");
    }

    return res.data; // Return server JSON { url, role, ... }
  } catch (err) {
    console.error("Upload error:", err);
    throw err;
  }
}

export async function deleteMedia(mediaId) {
  const res = await apiClient.delete(`/tours/media/delete-media/${mediaId}`);
  return res.data;
}

export function createStep({ tourId, title, lat, lng, kind }) {
  return withCustomerLimit({ tourId }, 'points', () => apiClient
    .post(`/tour/${tourId}/step`, { title, latitude: lat, longitude: lng, kind })
    .then((r) => r.data), { amount: !kind || kind === 'step' ? 1 : 0 });
}

export function reorderStepMedia({ stepId, order }) {
  return apiClient.post(`/tour/step/${stepId}/media/reorder`, { order }).then((r) => r.data);
}

// Bulk save all questions+options for a quiz step, in one language.
// payload: { language, questions: [{ id?, text, options: [{ id?, text, isCorrect }] }] }
export function saveQuiz({ stepId, payload }) {
  return apiClient.put(`/tour/step/${stepId}/quiz`, payload).then((r) => r.data);
}

// Ask the AI to generate wrong-answer distractors for a question.
// Returns { options: [string, string, string] }
export function generateQuizOptions({ tourId, language, questionText, correctAnswer, count = 3 }) {
  return apiClient
    .post(`/tour/${tourId}/ai/generate-quiz-options`, {
      language,
      questionText,
      correctAnswer,
      count,
    })
    .then((r) => r.data);
}

export function deleteStep(stepId) {
  return apiClient.delete(`/tour/step/${stepId}`).then((r) => r.data);
}

export function reorderSteps({ tourId, order }) {
  return apiClient.post(`/tour/${tourId}/step/reorder`, { order }).then((r) => r.data);
}

export function getTourLanguages(tourId) {
  return apiClient.get(`/tour/languages/${tourId}`).then((r) => r.data);
}

export function addTourLanguage({ tourId, code }) {
  return withCustomerLimit({ tourId }, 'languages',
    () => apiClient.post(`/tour/languages/${tourId}`, { code }).then((r) => r.data));
}

export function removeTourLanguage({ tourId, code }) {
  return apiClient.delete(`/tour/languages/${tourId}/${code}`).then((r) => r.data);
}

export function addVideo({ siteId, tourId, stepId, url, title, description, language }) {
  return apiClient
    .post(`/tours/media/add-video`, { siteId, tourId, stepId, url, title, description, language })
    .then((r) => r.data);
}

export function updateVideo({ mediaId, title, description, language }) {
  return apiClient
    .put(`/tours/media/update-video/${mediaId}`, { title, description, language })
    .then((r) => r.data);
}

export function generateAiTranslation({ tourId, stepId, sourceLanguage, targetLanguage, skipExisting = false }) {
  return apiClient
    .post(`/tour/${tourId}/ai/translate-step`, { stepId, sourceLanguage, targetLanguage, skipExisting })
    .then((r) => r.data);
}

export function generateAiNarration({ tourId, stepId, language, skipExisting = false, skipIfUnchanged = false }) {
  return apiClient
    .post(`/tour/${tourId}/ai/generate-audio`, { stepId, language, skipExisting, skipIfUnchanged })
    .then((r) => r.data);
}

export async function previewNarrationVoice({ tourId, voiceProfile }) {
  try {
    const response = await apiClient.post(
      `/tour/${tourId}/ai/preview-voice`,
      { voiceProfile },
      { responseType: "blob", timeout: 120000 },
    );
    return response.data;
  } catch (error) {
    const responseData = error.response?.data;
    if (responseData instanceof Blob) {
      try {
        error.response.data = JSON.parse(await responseData.text());
      } catch {
        // Leave non-JSON responses untouched.
      }
    }
    throw error;
  }
}

export function generateAiTourGuide({ tourId, stepId, language, text, persona }) {
  return apiClient
    .post(`/tour/${tourId}/ai/rewrite-step`, { stepId, language, text, persona })
    .then((r) => r.data);
}

export function enrichAiTourStep({ tourId, stepId, language, text, goal, instructions, targetSeconds, sourceIds, sourceConsent }) {
  return apiClient
    .post(`/tour/${tourId}/ai/enrich-step`, { stepId, language, text, goal, instructions, targetSeconds, sourceIds, sourceConsent })
    .then((r) => r.data);
}

// ----- Version history / restore -----

// List a tour's restore points (newest first).
export function getTourSnapshots(tourId) {
  return apiClient.get(`/tour/${tourId}/snapshots`).then((r) => r.data);
}

// Manually capture a "Checkpoint" of the tour's current content.
export function createTourSnapshot({ tourId, label }) {
  return apiClient.post(`/tour/${tourId}/snapshots`, { label }).then((r) => r.data);
}

// Roll the tour back to a snapshot, in place. Auto-backs-up current state first.
export function restoreTourSnapshot({ tourId, snapshotId }) {
  return withCustomerLimit({ tourId, snapshotId }, null, () => apiClient
    .post(`/tour/${tourId}/snapshots/${snapshotId}/restore`)
    .then((r) => r.data), { checkContent: true });
}

export function getTourAccessSummary(siteId) {
  return apiClient
    .get(`/admin/tour-access/summary`, { params: { siteId } })
    .then((r) => r.data);
}

export function getTourAccessEvents({ tourId, languageCode, eventType, take }) {
  const params = { tourId };
  if (languageCode) params.languageCode = languageCode;
  if (eventType) params.eventType = eventType;
  if (take) params.take = take;
  return apiClient.get(`/admin/tour-access/events`, { params }).then((r) => r.data);
}

// ----- Visitor progress through the tour -----

// Drop-off curve: how many sessions reached each POI, split by signal quality.
export function getTourProgressFunnel({ siteId, tourId, days }) {
  const params = { siteId };
  if (tourId) params.tourId = tourId;
  if (days) params.days = days;
  return apiClient
    .get(`/admin/tour-progress/funnel`, { params })
    .then((r) => r.data);
}

// Individual sessions, newest first.
export function getTourProgressSessions({ siteId, tourId, take }) {
  const params = { siteId };
  if (tourId) params.tourId = tourId;
  if (take) params.take = take;
  return apiClient
    .get(`/admin/tour-progress/sessions`, { params })
    .then((r) => r.data);
}

// How many people pressed each end-of-tour button.
export function getTourProgressButtons({ siteId, tourId, days }) {
  const params = { siteId };
  if (tourId) params.tourId = tourId;
  if (days) params.days = days;
  return apiClient
    .get(`/admin/tour-progress/buttons`, { params })
    .then((r) => r.data);
}

// What share of devices that downloaded a tour ever reported progress back.
export function getTourProgressCoverage({ siteId, tourId }) {
  const params = { siteId };
  if (tourId) params.tourId = tourId;
  return apiClient
    .get(`/admin/tour-progress/coverage`, { params })
    .then((r) => r.data);
}

export function getTourDownloadAttempts({ tourId, take }) {
  const params = { tourId };
  if (take) params.take = take;
  return apiClient
    .get(`/admin/tour-access/attempts`, { params })
    .then((r) => r.data);
}
